import { useCallback, useEffect, useState } from 'react';

import Fade from '@mui/material/Fade';

import { FilePreview } from '@vk-hr-tek/ui/FilePreview';
import { useInject } from '@vk-hr-tek/core/ioc';
import { Box } from '@vk-hr-tek/ui/Box';
import { useTheme } from '@vk-hr-tek/ui/Theme';

import { SettingsService } from '@app/gen/settings';
import { useSelector, useRole } from '@app/hooks';

import { selectSidebarState } from '../../slice';
import { TIMEOUT_FOR_FADE } from '../../constants';

import { LogoEmptyIcon } from './LogoEmptyIcon';
import { LogoEmptyIconText } from './LogoEmptyIconText';

type LogoIconVariants = '24' | '32' | '72' | '80';

interface LogoProps {
  variant?: LogoIconVariants;
  inSidebar?: boolean;
}

export const Logo = ({ variant = '32', inSidebar = false }: LogoProps) => {
  const [role] = useRole();
  const settingsService = useInject(SettingsService);
  const theme = useTheme();

  const sidebarState = useSelector(selectSidebarState);

  const [error, setError] = useState<Error>();

  const loadMainLogo = useCallback(async () => {
    const res = await settingsService.getCompanyLogo({
      query: { version: 'main' },
    });
    return res.file;
  }, [settingsService]);

  const loadCompactLogo = useCallback(async () => {
    const res = await settingsService.getCompanyLogo({
      query: { version: 'compact', mode: 'fallback' },
    });
    return res.file;
  }, [settingsService]);

  useEffect(() => {
    setError(undefined);
  }, [role, sidebarState]);

  if (error) {
    if (inSidebar) {
      return (
        <Box display="flex" alignItems="center" pl="4">
          <LogoEmptyIcon variant={variant} />

          {theme !== 'otp' && (
            <Fade in={sidebarState} timeout={TIMEOUT_FOR_FADE}>
              <Box display="flex" alignItems="center">
                <LogoEmptyIconText />
              </Box>
            </Fade>
          )}
        </Box>
      );
    } else {
      return <LogoEmptyIcon variant={variant} />;
    }
  }

  return (
    <>
      {sidebarState && (
        <FilePreview
          key={role}
          onLoad={loadMainLogo}
          hideLoader
          onError={setError}
          {...(inSidebar
            ? { maxHeight: '40px', maxWidth: '256px' }
            : { height: Number(variant) })}
        />
      )}
      {!sidebarState && (
        <FilePreview
          key={role}
          onLoad={loadCompactLogo}
          hideLoader
          onError={setError}
          {...(inSidebar
            ? { maxHeight: '40px', maxWidth: '40px' }
            : { height: Number(variant) })}
        />
      )}
    </>
  );
};
